<template>
	<div v-bind:class="['form', { transition }]">
		<template v-if="pageStep === 'default'">
			<h3 class="mfa-title">
				Login
				<HelpTooltip>
					Please enter your Email Address - the one you gave to the DoctorPlan team to setup your account.
					<br>
					<br>
					Please use the password you setup for DoctorPlan. If you can't remember it, you can always click on  'Forgot Password'.
				</HelpTooltip>
			</h3>
			<div class="auto-logout" v-if="autoLogoutSet">
				<CertifyIcon></CertifyIcon>
				<span>
					To protect your account and patient data, you have been automatically logged out of DoctorPlan - due to 30 minutes of inactivity.  Please log in again.
				</span>
			</div>
			<!-- Username input -->
			<EmailInput
				:autofocus="true"
				v-model="email"
				v-bind:error="emailError"
				@reset="emailError = null"
				@next="initiateLogin">
			</EmailInput>

			<!-- Password input -->
			<PasswordInput
				:autofocus="false"
				v-model="password"
				v-bind:error="passwordError"
				@reset="passwordError = null"
				@next="login">
			</PasswordInput>

			<router-link :to="{ name: 'reset', props: { email } }"
				class="forgot-password">
				Forgot password?
			</router-link>

			<!-- Login button -->
			<SubmitButton
				:disabled="loginDisabled"
				:loading="loginLoading"
				@click="login">
				{{ loginActionText }}
			</SubmitButton>
		</template>
		<template v-else-if="pageStep === 'mfa'">
			<h3 class="mfa-title">
				Two-Factor Authentication
				<HelpTooltip>
					For your security, two-factor authentication prevents someone unauthorized to access your account, even if they somehow get your password.  You are sent a unique code to your email / phone, which you are required to enter immediately, which gives you an extra layer of security.
				</HelpTooltip>
			</h3>
			<div class="para">
				For your security, we use 2-factor authentication, where you must enter a verification code sent to your email or phone.
				<br/>
				<br/>
				We have sent you the 6-digit code on {{ isEmailMfa ? 'email' : 'phone' }}
				<b style="font-size: 14px;">{{ isEmailMfa ? maskedEmail : maskedPhone }}</b>
				<div class="get-code" v-if="maskedEmail && maskedPhone" @click="getMfaElse()">
						Get 6-digit code on {{ isEmailMfa ? 'phone' : 'email' }}?
				</div>
			</div>
			<div class="para">
				Enter the code here
				<CodeInput v-model="mfaCode" @next="sendMfa"></CodeInput>
				<div :class="['mfa-error', { opaque : mfaError}]">
					{{ mfaError }}
				</div>
				<SubmitButton @click="sendMfa"
					:disabled="mfaCode.length !== 6"
					:loading="mfaLoading">
					Submit
				</SubmitButton>
				<span class="notice-para">
					If you don’t see the {{ isEmailMfa ? 'email in next few minutes, check your spam/junk folder. If you are still facing issues,' : 'SMS in next few minutes,' }}  contact
					<a href="mailto:support@doctorplan.com">support@doctorplan.com</a>.
				</span>
			</div>
		</template>
		<template v-else-if="pageStep === 'mfaChoice'">
			<h3 class="mfa-title">
				Two-Factor Authentication
				<HelpTooltip>
					For your security, two-factor authentication prevents someone unauthorized to access your account, even if they somehow get your password.  You are sent a unique code to your email / phone, which you are required to enter immediately, which gives you an extra layer of security.
				</HelpTooltip>
			</h3>
			<div class="para">
				For your security, we use 2-factor authentication, where you must enter a verification code sent to your email or phone.
				<br>
				<br>
				Please select where you want to receive the 6 digit verification code and click the button below

				<div class="choice-list">
					<div class="choice">
						Phone No.
						<label>
							<RadioCheckIcon v-if="choiceMfa === 'phone'"></RadioCheckIcon>
							<RadioUncheckIcon v-else></RadioUncheckIcon>
							<input type="radio" id="phone-choice" value="phone" v-model="choiceMfa">
							&nbsp;
							{{ maskedPhone }}
						</label>
					</div>
					<div class="choice">
						Email Address
						<label>
							<RadioCheckIcon v-if="choiceMfa === 'email'"></RadioCheckIcon>
							<RadioUncheckIcon v-else></RadioUncheckIcon>
							<input type="radio" id="email-choice" value="email" v-model="choiceMfa">
							&nbsp;
							{{ maskedEmail }}
						</label>
					</div>
				</div>
				<SubmitButton
					:loading="loginLoading"
					@click="login">
					Get Verification Code
				</SubmitButton>
				<span class="notice-para">
					If you don’t see the SMS/email in next few minutes, contact
					<a href="mailto:support@doctorplan.com">support@doctorplan.com</a>.
				</span>
			</div>
		</template>
		<template v-else-if="pageStep === 'changePassword'">
			<h3 class="mfa-title">
				Set a New Password
				<HelpTooltip>
					For your security, please set a password you will remember, and keep the password secure.
				</HelpTooltip>
			</h3>
			<br/>
			<PasswordInput
				title="Enter Password"
				:autofocus="false"
				v-model="newPassword"
				info="minimum 8 characters">
			</PasswordInput>
			<PasswordInput
				title="Re-enter Password"
				:autofocus="false"
				v-model="confirmPassword"
				:error="setPasswordError">
			</PasswordInput>
			<br/>
			<SubmitButton
				:disabled="setPasswordDisabled"
				:loading="setPasswordLoading"
				@click="setPassword">
				Continue
			</SubmitButton>
		</template>
	</div>
</template>

<script>
import AuthForm from '.'

export default {
	name: 'direct-login-form',
	extends: AuthForm,

	data() {
		return {
			choiceMfa: 'phone',

		}
	},


	props: {
		logout: {
			type: Boolean
		}
	},

	mounted() {
        if (this.query && this.query.token) {
            this.verifyReset({
                token: this.query.token
            })
            .then((result) => {
				this.success()
            })
            .catch((err) => {
				console.log(err)
            })
		}
		window.addEventListener('keypress', this.keyListener)
    },

	computed: {
		loginActionText() {
			return (this.authenticatedEmail) ? 'login' : 'Next'
		},
		loginSecondaryText() {
			return (this.authenticatedEmail) ? 'forgot password?' : ''
		},
		loginSecondary() {
			return (this.authenticatedEmail) ? '/reset' : '/login'
		},
		loginDisabled() {
			return this.email.length == 0 || this.password.length == 0
		},

	},

	beforeDestroy() {
		window.removeEventListener('keypress', this.keyListener)
	}
}
</script>

<style scoped lang="scss">
@import "style.scss";
</style>
